import Dexie, { type Table } from 'dexie';

/**
 * Status of a queued mutation. The lifecycle is:
 *
 *   pending  → queued, waiting for the next replay attempt
 *   replaying → currently being POSTed/PATCHed/DELETEd to the backend
 *   failed   → server returned a non-retryable error (4xx other than
 *              408/409/429); the mutation stays so the user can review
 *              and either retry manually (F11.4) or drop it.
 *
 * Successful mutations are deleted from the table after the replay
 * resolves — the IndexedDB store is bounded by activity, not size.
 */
export type QueuedMutationStatus = 'pending' | 'replaying' | 'failed';

export type QueuedMutationMethod = 'POST' | 'PATCH' | 'DELETE' | 'PUT';

/**
 * One row in the offline mutation queue. Body is stored as a JSON-
 * serializable value (axios accepts both arbitrary objects and bare
 * strings; we coerce to the JSON-friendly union here so Dexie can
 * cleanly persist it).
 *
 * `mutationKey` is optional and lets callers tag related mutations
 * (e.g. 'work-order:wo-1234:patch') for grouping in the queue UI; it
 * also enables a "latest-wins" strategy in F11.3 if the user edits
 * the same WO twice while offline.
 */
export interface QueuedMutation {
  id?: number;
  createdAt: string;
  status: QueuedMutationStatus;
  method: QueuedMutationMethod;
  /** Path including any leading slash, relative to the API root (axios baseURL). */
  url: string;
  body?: unknown;
  /** Extra headers (e.g. Idempotency-Key). Most callers leave this undefined. */
  headers?: Record<string, string>;
  /** Caller-defined grouping/idempotency tag. */
  mutationKey?: string;
  /** Number of replay attempts so far. */
  retries: number;
  /** Last server error message (free-form), set when status='failed'. */
  lastError?: string;
}

/**
 * IndexedDB store bound to the `isi-gmao-offline` database. We bump
 * `version(1)` here; future schema migrations bump the version and
 * provide a `.upgrade()` callback. Dexie handles the migration on
 * the next open call.
 */
class OfflineDb extends Dexie {
  mutations!: Table<QueuedMutation, number>;

  constructor() {
    super('isi-gmao-offline');
    this.version(1).stores({
      // Index on status + createdAt so a replay can pull pending rows
      // in chronological order without a table scan.
      mutations: '++id, status, createdAt, mutationKey',
    });
  }
}

export const offlineDb = new OfflineDb();
